import { useLegalEntities } from '../../../hooks/useLegalEntities';
import { useMovementTypes } from '../../../hooks/useMovementTypes';
import { useEffect, useMemo, useState } from 'react';
import { Box, Table, TableBody, TableHead, TableRow, Typography, MenuItem } from '@mui/material';
import { GetApp as ExportIcon, Search as SearchIcon } from '@mui/icons-material';

import { useBankMovementsByDateRange, useAccountBalance } from '../../../hooks/useBankMovements';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  TableBodySkeleton,
  CustomButton,
  CustomTextField,
  CustomAutoComplete,
  HorizontalForm,
  PaginationTable,
} from '../../../common/components';
import MovementRow from './MovementRow';
import { DEFAULT_PAGINATION } from '../constants/bankMovementsConstants';
import { formatCurrency, formatDate } from '../utils/formatters';
import useBankMovementsFiltersStore from '../../../stores/useBankMovementsFiltersStore';
import {
  COST_AREA_OPTIONS,
  COST_BEHAVIOR_OPTIONS,
  COST_TRACEABILITY_OPTIONS,
} from '../../constants/financeConstants';

const MovementsList = ({ account, onEditMovement }) => {
  const { data: legalEntities = [] } = useLegalEntities();
  const { data: movementTypes = [] } = useMovementTypes();
  const defaultFromDate = useMemo(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    []
  );
  const defaultToDate = useMemo(() => new Date().toISOString().split('T')[0], []);
  const todayDate = useMemo(() => new Date().toISOString().split('T')[0], []);

  const { getDateRange, setDateRange, hydrated } = useBankMovementsFiltersStore();

  const [draftFromDate, setDraftFromDate] = useState(defaultFromDate);
  const [draftToDate, setDraftToDate] = useState(defaultToDate);
  const [draftSearchTerm, setDraftSearchTerm] = useState('');
  const [draftReconciliationFilter, setDraftReconciliationFilter] = useState('all');
  const [draftTypeFilter, setDraftTypeFilter] = useState('all');
  const [draftCostBehaviorFilter, setDraftCostBehaviorFilter] = useState('all');
  const [draftCostTraceabilityFilter, setDraftCostTraceabilityFilter] = useState('all');
  const [draftCostAreaFilter, setDraftCostAreaFilter] = useState('all');

  const [submittedFromDate, setSubmittedFromDate] = useState(defaultFromDate);
  const [submittedToDate, setSubmittedToDate] = useState(defaultToDate);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [appliedReconciliationFilter, setAppliedReconciliationFilter] = useState('all');
  const [appliedTypeFilter, setAppliedTypeFilter] = useState('all');
  const [appliedCostBehaviorFilter, setAppliedCostBehaviorFilter] = useState('all');
  const [appliedCostTraceabilityFilter, setAppliedCostTraceabilityFilter] = useState('all');
  const [appliedCostAreaFilter, setAppliedCostAreaFilter] = useState('all');

  const [exportMovementsToExcelFn, setExportMovementsToExcelFn] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGINATION.rowsPerPage);

  const {
    data: movements = [],
    isLoading,
  } = useBankMovementsByDateRange({
    start_date: submittedFromDate,
    end_date: submittedToDate,
    account_id: account.fund_type === 'cash' ? undefined : account.uuid,
    cash_account_id: account.fund_type === 'cash' ? account.uuid : undefined,
    cost_behavior: appliedCostBehaviorFilter === 'all' ? undefined : appliedCostBehaviorFilter,
    cost_nature: appliedCostTraceabilityFilter === 'all' ? undefined : appliedCostTraceabilityFilter,
    cost_traceability: appliedCostTraceabilityFilter === 'all' ? undefined : appliedCostTraceabilityFilter,
    cost_area: appliedCostAreaFilter === 'all' ? undefined : appliedCostAreaFilter,
  });
  const { data: todayBalance, isLoading: isTodayBalanceLoading } = useAccountBalance(
    account?.uuid,
    todayDate,
    account?.fund_type || 'bank'
  );

  const filteredMovements = movements.filter((movement) => {
    const matchesSearch = (() => {
      if (!appliedSearchTerm) return true;
      const search = appliedSearchTerm.toLowerCase();
      const normalize = (value) => String(value ?? '').toLowerCase();
      return (
        normalize(movement.concept).includes(search) ||
        normalize(movement.voucher_number).includes(search) ||
        normalize(movement.legal_entity_name).includes(search) ||
        String(movement.amount ?? '').includes(appliedSearchTerm)
      );
    })();

    const matchesReconciliation =
      appliedReconciliationFilter === 'all' ||
      (appliedReconciliationFilter === 'reconciled' && movement.is_reconciled) ||
      (appliedReconciliationFilter === 'pending' && !movement.is_reconciled);

    const matchesType = appliedTypeFilter === 'all' || movement.uuid_movement_type === appliedTypeFilter;
    const matchesCostBehavior =
      appliedCostBehaviorFilter === 'all' || movement.cost_behavior === appliedCostBehaviorFilter;
    const matchesCostTraceability =
      appliedCostTraceabilityFilter === 'all' || (movement.cost_nature || movement.cost_traceability) === appliedCostTraceabilityFilter;
    const matchesCostArea = appliedCostAreaFilter === 'all' || movement.cost_area === appliedCostAreaFilter;

    return matchesSearch && matchesReconciliation && matchesType && matchesCostBehavior && matchesCostTraceability && matchesCostArea;
  });

  const totalCount = filteredMovements.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage) || 1);
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const paginatedMovements = filteredMovements.slice(startIndex, startIndex + rowsPerPage);

  const movementsWithDailyClosing = useMemo(() => {
    if (paginatedMovements.length === 0) return [];

    const getDateKey = (value) => {
      if (!value) return null;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        return trimmed.includes('T') ? trimmed.split('T')[0] : trimmed;
      }
      const dateObj = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(dateObj.getTime())) return null;
      return dateObj.toISOString().split('T')[0];
    };

    const rows = [];
    let lastMovementUpToToday = null;

    filteredMovements.forEach((movement) => {
      const movementDateKey = getDateKey(movement.due_date) || getDateKey(movement.emission_date);
      if (movementDateKey && movementDateKey <= todayDate) {
        lastMovementUpToToday = movement;
      }
    });

    paginatedMovements.forEach((movement) => {
      rows.push({ type: 'movement', movement, key: movement.uuid });

      if (lastMovementUpToToday && movement.uuid === lastMovementUpToToday.uuid) {
        rows.push({
          type: 'closing',
          key: `closing-${todayDate}-${movement.uuid}`,
        });
      }
    });

    return rows;
  }, [paginatedMovements, filteredMovements, todayDate]);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const resetDateRange = () => {
    setDraftFromDate(defaultFromDate);
    setDraftToDate(defaultToDate);
    if (hydrated && account?.uuid) {
      setDateRange(account.uuid, defaultFromDate, defaultToDate);
    }
  };

  const isRangeTooWide = (from, to) => {
    if (!from || !to) return false;
    const fromTime = new Date(from).getTime();
    const toTime = new Date(to).getTime();
    if (Number.isNaN(fromTime) || Number.isNaN(toTime)) return false;
    const diffMs = Math.abs(toTime - fromTime);
    const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
    return diffMs > ONE_YEAR_MS;
  };

  useEffect(() => {
    if (!hydrated || !account?.uuid) return;
    const stored = getDateRange(account.uuid);
    if (stored?.fromDate || stored?.toDate) {
      const candidateFrom = stored.fromDate || defaultFromDate;
      const candidateTo = stored.toDate || defaultToDate;
      if (isRangeTooWide(candidateFrom, candidateTo)) {
        resetDateRange();
      } else {
        setDraftFromDate(candidateFrom);
        setDraftToDate(candidateTo);
        setSubmittedFromDate(candidateFrom);
        setSubmittedToDate(candidateTo);
      }
    } else {
      setDateRange(account.uuid, defaultFromDate, defaultToDate);
      setDraftFromDate(defaultFromDate);
      setDraftToDate(defaultToDate);
      setSubmittedFromDate(defaultFromDate);
      setSubmittedToDate(defaultToDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.uuid, getDateRange, setDateRange, defaultFromDate, defaultToDate, hydrated]);

  useEffect(() => {
    let mounted = true;
    import('../utils/exportExcel').then(({ exportMovementsToExcel }) => {
      if (mounted) {
        setExportMovementsToExcelFn(() => exportMovementsToExcel);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const handleExportExcel = async () => {
    if (exportMovementsToExcelFn) {
      await exportMovementsToExcelFn(
        filteredMovements,
        `Movimientos_${account?.name || account?.account_number || 'cuenta'}.xlsx`
      );
    }
  };

  const handleFromDateChange = (event) => {
    const value = event.target.value;
    const newFromDate = value;
    const newToDate = draftToDate;

    if (isRangeTooWide(newFromDate, newToDate)) {
      resetDateRange();
    } else {
      setDraftFromDate(newFromDate);
      if (account?.uuid) {
        setDateRange(account.uuid, newFromDate, newToDate);
      }
    }
    setPage(1);
  };

  const handleToDateChange = (event) => {
    const value = event.target.value;
    const newFromDate = draftFromDate;
    const newToDate = value;

    if (isRangeTooWide(newFromDate, newToDate)) {
      resetDateRange();
    } else {
      setDraftToDate(newToDate);
      if (account?.uuid) {
        setDateRange(account.uuid, newFromDate, newToDate);
      }
    }
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setDraftSearchTerm(event.target.value);
    setPage(1);
  };

  const handleReconciliationChange = (event) => {
    setDraftReconciliationFilter(event.target.value);
    setPage(1);
  };

  const handleTypeChange = (event) => {
    setDraftTypeFilter(event.target.value);
    setPage(1);
  };

  const handleCostBehaviorChange = (event) => {
    setDraftCostBehaviorFilter(event.target.value);
    setPage(1);
  };

  const handleCostTraceabilityChange = (event) => {
    setDraftCostTraceabilityFilter(event.target.value);
    setPage(1);
  };

  const handleCostAreaChange = (event) => {
    setDraftCostAreaFilter(event.target.value);
    setPage(1);
  };

  const handleSearch = () => {
    setSubmittedFromDate(draftFromDate);
    setSubmittedToDate(draftToDate);
    setAppliedSearchTerm(draftSearchTerm);
    setAppliedReconciliationFilter(draftReconciliationFilter);
    setAppliedTypeFilter(draftTypeFilter);
    setAppliedCostBehaviorFilter(draftCostBehaviorFilter);
    setAppliedCostTraceabilityFilter(draftCostTraceabilityFilter);
    setAppliedCostAreaFilter(draftCostAreaFilter);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
      }}
    >
      <HorizontalForm>
        <CustomTextField
          label="Desde"
          type="date"
          value={draftFromDate}
          onChange={handleFromDateChange}
          size="medium"
          sx={{ maxWidth: 180, mr: 2 }}
          shrinkLabel
        />

        <CustomTextField
          label="Hasta"
          type="date"
          value={draftToDate}
          onChange={handleToDateChange}
          size="medium"
          sx={{ maxWidth: 180, mr: 2 }}
          shrinkLabel
        />

        <CustomTextField
          placeholder="Buscar por concepto, comprobante o beneficiario"
          value={draftSearchTerm}
          onChange={handleSearchChange}
          size="medium"
          sx={{ maxWidth: 430, mr: 2 }}
        />

        <CustomAutoComplete
          label="Conciliado"
          value={draftReconciliationFilter}
          onChange={handleReconciliationChange}
          size="medium"
          sx={{ maxWidth: 150, mr: 2 }}
        >
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="reconciled">Si</MenuItem>
          <MenuItem value="pending">No</MenuItem>
        </CustomAutoComplete>

        <CustomAutoComplete
          label="Tipo"
          value={draftTypeFilter}
          onChange={handleTypeChange}
          size="medium"
          sx={{ maxWidth: 200, mr: 2 }}
        >
          <MenuItem value="all">Todos</MenuItem>
          {movementTypes
            .filter((type) => type.is_active !== false)
            .map((type) => (
              <MenuItem key={type.uuid} value={type.uuid}>
                {type.description}
              </MenuItem>
            ))}
        </CustomAutoComplete>

        <CustomAutoComplete
          label="Fijo/Variable"
          value={draftCostBehaviorFilter}
          onChange={handleCostBehaviorChange}
          size="medium"
          sx={{ maxWidth: 170, mr: 2 }}
        >
          <MenuItem value="all">Todos</MenuItem>
          {COST_BEHAVIOR_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </CustomAutoComplete>

        <CustomAutoComplete
          label="Ordinario/Extraordinario"
          value={draftCostTraceabilityFilter}
          onChange={handleCostTraceabilityChange}
          size="medium"
          sx={{ maxWidth: 190, mr: 2 }}
        >
          <MenuItem value="all">Todos</MenuItem>
          {COST_TRACEABILITY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </CustomAutoComplete>

        <CustomAutoComplete
          label="Área"
          value={draftCostAreaFilter}
          onChange={handleCostAreaChange}
          size="medium"
          sx={{ maxWidth: 170, mr: 2 }}
        >
          <MenuItem value="all">Todas</MenuItem>
          {COST_AREA_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </CustomAutoComplete>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            ml: { md: 'auto' },
            mt: { xs: 2, md: 0 },
            alignSelf: { md: 'flex-end' },
            alignItems: 'center',
          }}
        >
          <CustomButton
            onClick={handleSearch}
            size="medium"
            buttonIcon={SearchIcon}
            sx={{ minHeight: 60 }}
          />

          <CustomButton
            variant="info"
            buttonIcon={ExportIcon}
            onClick={handleExportExcel}
            size="small"
            sx={{ minHeight: 60 }}
          />
        </Box>
      </HorizontalForm>

      <GeneralTableBox>
        <Table size="small">
          <TableHead>
            <TableRow>
              <HeadTableCell>Fecha emi.</HeadTableCell>
              <HeadTableCell>Fecha venc.</HeadTableCell>
              <HeadTableCell>Tipo</HeadTableCell>
              <HeadTableCell>Concepto</HeadTableCell>
              <HeadTableCell>N° OP</HeadTableCell>
              <HeadTableCell>N° comp.</HeadTableCell>
              <HeadTableCell>Beneficiario</HeadTableCell>
              <HeadTableCell>Clasificación</HeadTableCell>
              <HeadTableCell>Importe</HeadTableCell>
              <HeadTableCell>Saldo Libro</HeadTableCell>
              <HeadTableCell>Acciones</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodySkeleton />
            ) : (
              <>
              {movementsWithDailyClosing.map((row) =>
              row.type === 'movement' ? (
                <MovementRow
                  key={row.key}
                  movement={row.movement}
                  movementTypes={movementTypes}
                  legalEntities={legalEntities}
                  onEdit={onEditMovement}
                />
              ) : (
                <TableRow key={row.key} sx={{ backgroundColor: 'grey.200' }}>
                  <RowTableCell colSpan={11}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        gap: 2,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                      }}
                    >
                      <Typography fontWeight="bold" sx={{ ml: 10, py: 2 }}>
                        Saldo real al {formatDate(todayDate)}:{' '}
                        <Box component="span" sx={{ fontFamily: 'monospace' }}>
                          {isTodayBalanceLoading
                            ? '-'
                            : formatCurrency(todayBalance?.real_balance ?? 0, 2)}
                        </Box>
                      </Typography>
                      <Typography fontWeight="bold" sx={{ mr: 10, py: 2 }}>
                        Saldo libro al {formatDate(todayDate)}:{' '}
                        <Box component="span" sx={{ fontFamily: 'monospace' }}>
                          {isTodayBalanceLoading
                            ? '-'
                            : formatCurrency(todayBalance?.book_balance ?? 0, 2)}
                        </Box>
                      </Typography>
                    </Box>
                  </RowTableCell>
                </TableRow>
              )
            )}
            {filteredMovements.length === 0 && (
              <TableRow>
                <RowTableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron movimientos
                  </Typography>
                </RowTableCell>
              </TableRow>
            )}
              </>
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>

      {totalCount > rowsPerPage && (
        <PaginationTable
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(1);
          }}
          sx={{ mt: 2 }}
        />
      )}
    </Box>
  );
};

export default MovementsList;
